// ImageUploadComponent.tsx
import { useEffect, useState } from "react";
import { Form, Row, Col, Button, Spinner } from "react-bootstrap";
import { get, set } from "idb-keyval";
import { formLabelTranslations, tbo } from "./translation";

interface ImageItem {
  file: File;
  preview: string;
  thumbnail: string;
}

async function createThumbnail(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === "string") {
        img.src = reader.result;
      }
    };
    img.onload = () => {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      const size = 150;
      canvas.width = size;
      canvas.height = size;
      if (ctx) {
        ctx.drawImage(img, 0, 0, size, size);
        resolve(canvas.toDataURL("image/jpeg", 0.7));
      } else {
        reject("Canvas not supported");
      }
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export default function ImageUploadComponent({ formik, language, resetTrigger }: { formik: any, language: string,   resetTrigger: number  }) {
  const [images, setImages] = useState<ImageItem[]>([]);
  const [isUploading, setIsUploading] = useState(false);

  useEffect(() => {
    console.log('resetting images');
    setImages([]);
    formik.setFieldValue("images", []);
  }, [resetTrigger]);

  useEffect(() => {
    get("storedImages").then((files: File[] | undefined) => {
      if (files && Array.isArray(files)) {
        Promise.all(files.map(async (file) => ({
          file,
          preview: URL.createObjectURL(file),
          thumbnail: await createThumbnail(file),
        }))).then((restored) => {
          setImages(restored);
          formik.setFieldValue("images", restored.map(({ file }) => ({
            name: file.name,
            size: file.size,
            type: file.type,
          })));
        });
      }
    });
  }, []);

  const saveToIndexedDB = (images: ImageItem[]) => {
    const files = images.map((img) => img.file);
    set("storedImages", files);
  };

  const handleChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files) return;

    const files = Array.from(event.target.files);
    const invalidFiles: string[] = [];

    files.forEach(file => {
      // Prüfe Dateityp
      if (!file.type.startsWith('image/')) {
        invalidFiles.push(`${file.name} ist keine Bilddatei`);
        return;
      }

      // Prüfe Dateigröße
      if (file.size > 15 * 1024 * 1024) {
        invalidFiles.push(`${file.name} ist zu groß (max. 15MB)`);
        return;
      }
    });

    if (invalidFiles.length > 0) {
      alert(tbo(language, {
        de: `Folgende Dateien wurden abgelehnt:\n${invalidFiles.join('\n')}`,
        en: `Following files were rejected:\n${invalidFiles.join('\n')}`,
        fr: `Les fichiers suivants ont été rejetés:\n${invalidFiles.join('\n')}`
      }));
      event.target.value = "";
      return;
    }

    setIsUploading(true);

    const validFiles = files.slice(0, 3 - images.length);
    const newImages = await Promise.all(
        validFiles.map(async (file) => ({
          file,
          preview: URL.createObjectURL(file),
          thumbnail: await createThumbnail(file),
        }))
    );

    const updated = [...images, ...newImages];
    setImages(updated);
    saveToIndexedDB(updated);

    const meta = updated.map(({ file }) => ({
      name: file.name,
      size: file.size,
      type: file.type,
    }));
    formik.setFieldValue("images", meta);

    setIsUploading(false);
    event.target.value = "";
  };

  const handleRemove = (index: number) => {
    URL.revokeObjectURL(images[index].preview);
    const updated = images.filter((_, i) => i !== index);
    setImages(updated);
    saveToIndexedDB(updated);

    const meta = updated.map(({ file }) => ({
      name: file.name,
      size: file.size,
      type: file.type,
    }));
    formik.setFieldValue("images", meta);
  };

  return (
    <Form.Group className="mb-3" as={Row}>
      <Form.Label column sm={3}>{tbo(language, formLabelTranslations.images)}</Form.Label>
      <Col sm={9}>
        <Form.Control
          type="file"
          accept="image/*"
          multiple
          onChange={handleChange}
          disabled={images.length >= 3 || isUploading}
          isInvalid={formik.touched.images && Boolean(formik.errors.images)}
        />
        <Form.Control.Feedback type="invalid">
          {formik.errors.images}
        </Form.Control.Feedback>

        {isUploading && (
          <div className="mt-2 text-primary d-flex align-items-center">
            <Spinner animation="border" role="status" size="sm" className="me-2" />
            Uploading...
          </div>
        )}

        <div className="row mt-2">
          {images.map((image, index) => (
            <div className="col-4 mb-2" key={index}>
              <div className="position-relative">
                <img
                  src={image.thumbnail}
                  className="img-thumbnail"
                  alt={`Bild ${index + 1}`}
                />
                <Button
                  variant="danger"
                  size="sm"
                  className="position-absolute top-0 end-0 m-1"
                  onClick={() => handleRemove(index)}
                >
                  &times;
                </Button>
              </div>
            </div>
          ))}
        </div>
      </Col>
    </Form.Group>
  );
}
